/**
 * SmartTemplates — Trade & Category navigator for the Templates tab.
 * Mirrors SmartCalculators in structure: Trade dropdown → 17 category cards
 * → per-category template list. Templates and the matching calculator pull
 * from the same per-category bundle, so material, labor, and price data
 * stay consistent across the two surfaces.
 */

import React, { useMemo, useState } from 'react';
import { useIdioma } from '../../i18n/idioma';
import toast from 'react-hot-toast';
import { ArrowLeft, BookmarkPlus, Plus, Pencil, Trash2 } from 'lucide-react';
import { EstimateItem, EstimateZone, PrivateTemplate } from '../../types';
import { CATEGORIES_BY_TRADE } from '../../data/categories/registry';
import { DEFAULT_TRADE_ID } from '../../data/trades';
import { useEstimates } from '../../contexts/EstimatesContext';
import { useConfirm } from '../../contexts/DialogContext';
import { usePrivateTemplates, useDeletePrivateTemplate } from '../../hooks/useEstimateQueries';
import TradeSelect from './category-tools/TradeSelect';
import CategoryGrid from './category-tools/CategoryGrid';
import CategoryTemplatesView from './category-tools/CategoryTemplatesView';
import PrivateTemplateEditor from './PrivateTemplateEditor';
import { countUnpricedItems, getFirstUnpricedItem } from '../../utils/estimatePricingGaps';
import PricingNextStepCard from './PricingNextStepCard';
import PricingNextStepModal from './PricingNextStepModal';
import { Button } from '../ui/Button';
import { SegmentedControl } from '../ui/SegmentedControl';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonRows } from '../ui/Skeleton';

interface SmartTemplatesProps {
  /** true en la página de Plantillas; false (por defecto) en el cajón. */
  enPagina?: boolean;
  /** Drops a template's rows into the estimate. Default behavior auto-creates
   *  a zone named after the template; pass `targetZoneId` to merge into an
   *  existing zone instead. */
  onAddTemplateItems: (items: EstimateItem[], template: { id: string; name: string }, targetZoneId?: string) => void;
  /** Workspace target margin (percent, 0..99). Drives labor/other line pricing on
   *  rows dropped from a v2 template. */
  targetMarginPercent: number;
  /** Workspace default material markup (percent). Drives MATERIAL line pricing on
   *  rows dropped from a v2 template. */
  materialMarkupPercent: number;
  /** Existing estimate zones — surfaced in each template card's zone dropdown. */
  zones: EstimateZone[];
  /** Workspace overhead inputs — labor lines fall back to this when no
   *  pricebook/role rate matches, so labor never drops in at $0. */
  laborOverhead: {
    baseLaborCost: number;
    laborBurdenPercent: number;
    overheadPerManHour: number;
    breakEvenRate?: number;
  };
  /** Optional: parent can ask the panel to switch to the Calculators tab. */
  onRequestCalculator?: (categoryId: string) => void;
  /** Pre-select a trade when the estimate was created with a specific work type. */
  initialTradeId?: string;
  /** Pre-select category from Start a Job scope step. */
  initialScopeCategoryId?: string;
  /** Line items already on the estimate — gates stacking templates before pricing. */
  existingItems?: EstimateItem[];
  businessState?: string;
  onFocusUnpriced?: () => void;
}

type TemplateMode = 'global' | 'private';

/**
 * `enPagina` — dos sitios, dos comportamientos opuestos.
 *
 * En el CAJÓN del constructor de estimados esto vive dentro de una columna de
 * alto fijo: ahí su propio scroll es lo correcto, porque la página de detrás no
 * se mueve.
 *
 * En la PÁGINA de Plantillas (Lista de precios › Plantillas) es al revés: la
 * que scrollea es la página, y una caja con scroll propio se come la rueda. Con
 * el cursor encima de la tarjeta blanca no se podía bajar hasta salirse al
 * gris — el mismo fallo que tenía Clientes.
 *
 * Por eso es una prop y no un arreglo a secas: quitarle el scroll al cajón lo
 * habría roto.
 */
const SmartTemplates: React.FC<SmartTemplatesProps> = ({
  enPagina = false,
  onAddTemplateItems, targetMarginPercent, materialMarkupPercent, zones, laborOverhead, onRequestCalculator,
  initialTradeId, initialScopeCategoryId, existingItems, businessState, onFocusUnpriced,
}) => {
  const { t } = useIdioma();
  const { confirm } = useConfirm();
  const [mode, setMode] = useState<TemplateMode>('global');
  const [tradeId, setTradeId] = useState<string>(initialTradeId || DEFAULT_TRADE_ID);
  const [categoryId, setCategoryId] = useState<string | null>(initialScopeCategoryId ?? null);
  /** Set when the contractor clicks the Edit (pencil) button on a private
   *  template card. Opens the right-side drawer for direct mutation; this
   *  is the only path that changes private_templates.items. */
  const [editingTemplate, setEditingTemplate] = useState<PrivateTemplate | null>(null);
  const [pendingPrivate, setPendingPrivate] = useState<PrivateTemplate | null>(null);
  // T-2: standalone "create a new line template" flow (blank draft + isNew).
  const [creating, setCreating] = useState(false);

  const { pricebook } = useEstimates();
  const { data: privateTemplates = [], isLoading: privateLoading } = usePrivateTemplates();
  const deletePrivateMut = useDeletePrivateTemplate();

  // Índice del pricebook del taller por la llave que usan las plantillas.
  // `catalogItemId` ES esa llave: casa directo con `pricebookItemHint`.
  //
  // Solo entran filas con costo real (> 0). resolveCost pregunta con `.has()`,
  // no con `> 0`, así que una entrada en $0 ganaba el nivel 1 y tapaba TODOS
  // los respaldos de abajo — la partida caía al presupuesto a cero y sin
  // marcarse como pendiente de precio.
  //
  // Se fue la rama heredada que slugificaba `item.name`: era inalcanzable, no
  // rara. El slugificador convierte cualquier cosa que no sea [a-z0-9] en un
  // guion, incluido el punto, y las 1.149 llaves distintas del corpus llevan
  // punto. slug("landscaping.compost-cu-yd") da "landscaping-compost-cu-yd",
  // que no casa con nada. Cero aciertos posibles.
  const pricebookByHint = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of pricebook) {
      if (!item.catalogItemId) continue;
      const cost = Number(item.cost);
      if (Number.isFinite(cost) && cost > 0) map.set(item.catalogItemId, cost);
    }
    return map;
  }, [pricebook]);

  const categories = CATEGORIES_BY_TRADE[tradeId] ?? [];
  // Resolve active category from the CURRENT trade's list — not a
  // hardcoded landscaping map. Otherwise roofing/concrete/etc. clicks
  // would never drill in.
  const activeCategory = categoryId ? (categories.find(c => c.id === categoryId) ?? null) : null;
  const unpricedCount = countUnpricedItems(existingItems ?? []);
  const nextUnpriced = getFirstUnpricedItem(existingItems ?? []);

  const addPrivateTemplate = (pt: PrivateTemplate) => {
    const items = (Array.isArray(pt.items) ? pt.items : []).map((i: any) => ({
      ...i,
      id: Math.random().toString(36).substr(2, 9),
    })) as EstimateItem[];
    onAddTemplateItems(items, { id: pt.id, name: pt.name });
    toast.success(`${t('plantillas.toast.agregado')} "${pt.name}" — ${items.length} line${items.length === 1 ? '' : 's'} ${t('plantillas.toast.zonanueva')}`);
  };

  /* Vacío en la página: la rueda es de la página. Caja con scroll en el cajón. */
  const cajaScroll = enPagina ? '' : 'flex-1 min-h-0 overflow-y-auto custom-scrollbar';

  return (
    <>
    <div className={`flex flex-col gap-3 bg-acero-50 p-4 ${enPagina ? '' : 'h-full min-h-0'}`}>
      {/* Global / Private toggle — always visible at the top of the Templates panel. */}
      <SegmentedControl
        variant="pill"
        className="self-start"
        options={[
          { value: 'global', label: t('plantillas.tab.globales') },
          { value: 'private', label: t('plantillas.tab.privadas') },
        ]}
        value={mode}
        onChange={(v) => { setMode(v as TemplateMode); setCategoryId(null); }}
      />

      {unpricedCount > 0 && (
        <PricingNextStepCard
          variant="sidebar"
          unpricedCount={unpricedCount}
          nextItemLabel={nextUnpriced?.description}
          onShowNext={() => onFocusUnpriced?.()}
        />
      )}

      {mode === 'private' ? (
        <>
          <Button
            type="button"
            variant="secondary"
            className="shrink-0 w-full"
            onClick={() => setCreating(true)}
            leftIcon={<Plus size={14} />}
          >
            {t('plantillas.boton.nueva')}
          </Button>
          {privateLoading ? (
          <div className="flex-1 pt-4">
            <SkeletonRows rows={4} />
          </div>
        ) : privateTemplates.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <EmptyState
              icon={<BookmarkPlus size={18} />}
              title={t('plantillas.vacio.titulo')}
              description={t('plantillas.vacio.texto')}
            />
          </div>
        ) : (
          <div className={`pr-1 space-y-2 pb-10 ${cajaScroll}`}>
            {privateTemplates.map(pt => {
              const lineCount = Array.isArray(pt.items) ? pt.items.length : 0;
              return (
                <div key={pt.id} className="bg-white border border-acero-200 rounded-2xl p-3.5 transition-[border-color] duration-150 hover:border-acero-300">
                  <div className={`flex items-start justify-between gap-2 ${enPagina ? '' : 'mb-2'}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-navy-950 text-sm truncate">{pt.name}</p>
                      <p className="label-ui mt-0.5"><span className="font-data">{lineCount}</span> line{lineCount === 1 ? '' : 's'}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => setEditingTemplate(pt)}
                        className="p-1.5 text-acero-400 hover:text-navy-950 hover:bg-acero-100 rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                        title={t('plantillas.boton.editar.title')}
                        aria-label={t('plantillas.boton.editar.aria')}
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        onClick={async () => {
                          const ok = await confirm({
                            title: t('plantillas.borrar.pregunta'),
                            message: `"${pt.name}" will be removed from your private library. Estimates already built from it are not affected.`,
                            confirmText: t('plantillas.borrar.si'),
                            cancelText: t('comun.cancelar'),
                            type: 'danger',
                          });
                          if (!ok) return;
                          deletePrivateMut.mutate(pt.id, {
                            onSuccess: () => toast.success(t('plantillas.borrada', { nombre: pt.name })),
                            onError: () => toast.error(t('plantillas.toast.errorborrar')),
                          });
                        }}
                        className="p-1.5 text-acero-400 hover:text-peligro hover:bg-peligro-bg rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                        title={t('plantillas.boton.borrar.title')}
                        aria-label={t('plantillas.boton.borrar.aria')}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  {/* «Agregar al estimado» solo tiene sentido en el CAJÓN del
                      constructor, donde hay un estimado vivo que reciba las filas.
                      En la PÁGINA (Lista de precios › Plantillas) no lo hay: el
                      botón solo sacaba un aviso de «ve al constructor», y desde
                      ahí parecía que la plantilla creaba un estimado nuevo. Aquí
                      una plantilla privada se edita (lápiz) o se borra, y ya. */}
                  {!enPagina && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        if (unpricedCount > 0) {
                          setPendingPrivate(pt);
                          return;
                        }
                        addPrivateTemplate(pt);
                      }}
                      leftIcon={<Plus size={13} />}
                    >
                      {t('plantillas.boton.agregar')}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </>
      ) : !activeCategory ? (
        <>
          <TradeSelect value={tradeId} onChange={(id) => { setTradeId(id); setCategoryId(null); }} />
          <div className={`pr-1 pb-10 ${cajaScroll}`}>
            <CategoryGrid
              categories={categories}
              onSelect={(id) => setCategoryId(id)}
              mode="templates"
            />
          </div>
        </>
      ) : (
        <>
          <Button
            variant="ghost"
            size="sm"
            className="self-start"
            onClick={() => setCategoryId(null)}
            leftIcon={<ArrowLeft size={13} />}
          >
            {t('plantillas.boton.todascategorias')}
          </Button>
          <div className={`bg-white rounded-2xl border border-acero-200 pb-10 ${cajaScroll}`}>
            <CategoryTemplatesView
              tradeId={tradeId}
              category={activeCategory}
              targetMarginPercent={targetMarginPercent}
              materialMarkupPercent={materialMarkupPercent}
              pricebookByHint={pricebookByHint}
              laborOverhead={laborOverhead}
              zones={zones}
              onAddTemplateItems={onAddTemplateItems}
              existingItems={existingItems}
              businessState={businessState}
              onFocusUnpriced={onFocusUnpriced}
              onOpenCalculator={onRequestCalculator ? () => onRequestCalculator(activeCategory.id) : undefined}
            />
          </div>
        </>
      )}
    </div>

    {/* Edit Private Template drawer — the ONLY path that mutates a private
        template's items. Inline estimate edits do not feed back here. */}
    {(editingTemplate || creating) && (
      <PrivateTemplateEditor
        isOpen
        isNew={creating}
        template={editingTemplate ?? { id: 'new', workspaceId: '', name: '', items: [] }}
        targetMarginPercent={targetMarginPercent}
        materialMarkupPercent={materialMarkupPercent}
        laborOverhead={laborOverhead}
        onClose={() => { setEditingTemplate(null); setCreating(false); }}
      />
    )}

    <PricingNextStepModal
      open={!!pendingPrivate}
      unpricedCount={unpricedCount}
      nextItemLabel={nextUnpriced?.description}
      pendingLabel={pendingPrivate?.name}
      onShowNext={() => {
        setPendingPrivate(null);
        onFocusUnpriced?.();
      }}
      onContinueAnyway={() => {
        if (pendingPrivate) addPrivateTemplate(pendingPrivate);
        setPendingPrivate(null);
      }}
      onClose={() => setPendingPrivate(null)}
    />
    </>
  );
};

export default SmartTemplates;
